import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getSortedRowModel,
  ExpandedState,
  SortingState,
  Row,
  ColumnDef,
  RowSelectionState,
  ColumnPinningState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  useMemo,
  useState,
  useCallback,
  Fragment,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import classNames from 'classnames';
import {
  Button,
  Checkbox,
  DataTableSkeleton,
  InlineLoading,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from '@carbon/react';
import { ChevronRight, ChevronDown, Edit } from '@carbon/react/icons';
import {
  BulkEditSlotsPanelConnected,
  type BulkEditFormState,
  type BulkEditSavePayload,
} from './bulk-edit-slots';

type BulkEditSavePayloadHandler = (payload: BulkEditSavePayload) => void;
type BulkEditOriginalResolver = (
  row: TractNRI,
  key: keyof BulkEditFormState
) => string | undefined;
import { DndContext, DragMoveEvent, closestCenter } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import NoDataSvg from 'images/srp-illustration-data.svg';
import {
  TableActions,
  ServerExportParams,
  type ExportPreset,
} from 'components/table/table-actions';
import { useColumnFilter } from 'lib/hooks/useColumnFilter';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import { useColumnState } from 'lib/hooks/useColumnState';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import { ColumnFilter } from 'components/table/column-filter';
import { HiddenColumns } from 'components/table/hidden-columns';
import { MIN_WIDTH } from 'components/table/column-actions';
import { EmptyState } from 'components/empty-state';
import {
  DragHeader,
  getPinningStyles,
  getInitialColumnOrder,
  handleDragEnd,
  handleDragMove,
} from 'components/table/draggable';
import { DECIMAL_FORMATTER } from 'lib/ui';
import plur from 'plur';
import styles from './nri-table.module.scss';
import {
  AfeSourceConfig,
  DEFAULT_SUBTRACT_COLUMNS,
  DEFAULT_TRACT_COLUMNS,
  SubtractNRI,
  SubtractNRIColumns,
  TractNRI,
  TractNRIColumns,
  VolumeSourceConfig,
} from './types';
import {
  CELL_BASE_ROW_HEIGHT,
  CELL_HEIGHT,
  CELL_MARGIN,
  CELL_PADDING,
} from '../../pages/crm-tracts-subtracts/utils';
import {
  tractNRIColumnConfig,
  subtractNRIColumnConfig,
  getVerificationStatusText,
} from './utils';
import { rollupVerificationAtomFamily } from './atoms';
import { useAtomValue, useStore } from 'jotai';
import { tractEnumLoadableAtom } from 'pages/crm-tracts-subtracts/atoms';
import { TractWizardModal as TractWizard } from 'pages/crm-tracts-subtracts/tract-wizard';

const tractColumnHelper = createColumnHelper<TractNRI>();
const subtractColumnHelper = createColumnHelper<SubtractNRI>();

export const CellRenderer = (props: {
  children: React.ReactNode;
  href?: string;
}) => {
  const content = (
    <span className={styles.cellLabel}>{props.children ?? '--'}</span>
  );

  if (!props.href) {
    return content;
  }

  return (
    <a href={props.href} target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  );
};

const getColumnStyle = (size: number) => ({
  width: `${size}px`,
  minWidth: `${size}px`,
  maxWidth: `${size}px`,
});

const getSelectColumn = (): ColumnDef<TractNRI, any> => ({
  id: 'select',
  header: () => null,
  cell: ({ row }: { row: Row<TractNRI> }) => (
    <div className={styles.selectCell}>
      <Checkbox
        id={`nri-table-select-${row.id}`}
        labelText="Select row"
        hideLabel
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onClick={(e) => e.stopPropagation()}
        onChange={(_, { checked }: { checked: boolean }) =>
          row.toggleSelected(checked)
        }
      />
    </div>
  ),
  size: 56,
  enableColumnFilter: false,
  enableGlobalFilter: false,
  enableSorting: false,
  enableResizing: false,
  enableHiding: false,
  meta: {
    toggleColumnFilter: false,
    isColumnExportable: false,
  },
});

const getExpanderColumn = (
  showExpander: boolean
): ColumnDef<TractNRI, any> => ({
  id: 'expander',
  header: () => null,
  cell: ({ row }: { row: Row<TractNRI> }) => {
    if (!showExpander) return null;
    const canExpand = (row.original.subtracts?.length ?? 0) > 0;
    const isExpanded = row.getIsExpanded();
    return canExpand ? (
      <>
        {isExpanded ? (
          <ChevronDown
            onClick={(e) => {
              e.stopPropagation();
              row.getToggleExpandedHandler()();
            }}
            cursor={'pointer'}
          />
        ) : (
          <ChevronRight
            onClick={(e) => {
              e.stopPropagation();
              row.getToggleExpandedHandler()();
            }}
            cursor={'pointer'}
          />
        )}
      </>
    ) : null;
  },
  size: 50,
  enableColumnFilter: false,
  enableGlobalFilter: false,
  meta: {
    toggleColumnFilter: false,
    isColumnExportable: false,
  },
});

const getTractNRIColumns = (
  columnKeys: Array<TractNRIColumns>,
  showExpander: boolean,
  showSelectColumn: boolean
) => {
  const columnDefs: ColumnDef<TractNRI, any>[] = [];
  if (showSelectColumn) {
    columnDefs.push(getSelectColumn());
  }
  if (showExpander) {
    columnDefs.push(getExpanderColumn(showExpander));
  }
  columnKeys.forEach((column) => {
    columnDefs.push(
      tractColumnHelper.accessor(
        column,
        tractNRIColumnConfig[
          column as keyof typeof tractNRIColumnConfig
        ] as Parameters<typeof tractColumnHelper.accessor>[1]
      )
    );
  });
  return columnDefs;
};

const getSubtractNRIColumns = (columnKeys: Array<SubtractNRIColumns>) => {
  const columnDefs: ColumnDef<SubtractNRI, any>[] = [];
  columnKeys.forEach((column) => {
    columnDefs.push(
      subtractColumnHelper.accessor(
        column,
        subtractNRIColumnConfig[column] as Parameters<
          typeof subtractColumnHelper.accessor
        >[1]
      )
    );
  });
  return columnDefs;
};

const SubtractNRITable = ({
  subtractRows,
  subtractColumns,
  onTractClick,
}: {
  subtractRows: SubtractNRI[];
  subtractColumns: Array<SubtractNRIColumns>;
  onTractClick?: (tractId: string, subtractId?: string) => void;
}) => {
  // Use the first subtract item to determine which columns to show
  // All subtracts in the array should have the same structure
  const columns = useMemo(
    () => getSubtractNRIColumns(subtractColumns),
    [subtractColumns]
  );

  const subtractTable = useReactTable({
    data: subtractRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      reset: {
        resetCustomizations: () => {},
      },
      onTractClick,
    },
  });

  // Calculate total width based on column sizes
  const totalWidth = columns.reduce((sum, col) => sum + (col.size || 200), 0);

  return (
    <div
      className={styles.subtractTableWrapper}
      style={{ width: `${totalWidth}px` }}
    >
      <Table>
        <TableHead>
          {subtractTable.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeader
                  key={header.id}
                  style={getColumnStyle(header.column.getSize())}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHeader>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {subtractTable.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{
                    ...getColumnStyle(cell.column.getSize()),
                    // padding: '12px 0.75rem',
                  }}
                >
                  <div>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const FullNRITable = ({
  tractNRIRows,
  isLoading,
  tableId,
  subtractNRIColumns = DEFAULT_SUBTRACT_COLUMNS,
  tractNRIColumns = DEFAULT_TRACT_COLUMNS,
  selectedRow,
  onRowSelected,
  onRowsSelectionChange,
  volumeSourceConfig,
  afeSourceConfig,
  onServerExport,
  isServerExporting,
  exportPresets,
  hasMore,
  onLoadMore,
  isFetchingMore,
  onSearchChange,
  searchValue,
  onBulkEditSave,
  resolveBulkEditOriginal,
  enableSlotSelection = false,
  packageId,
}: {
  tableId: string;
  isLoading: boolean;
  tractNRIRows: TractNRI[];
  tractNRIColumns?: Array<TractNRIColumns>;
  subtractNRIColumns?: Array<SubtractNRIColumns>;
  selectedRow?: TractNRI;
  onRowSelected?: (row: TractNRI | null) => void;
  /** Callback fired with all selected rows when multi-row selection changes. */
  onRowsSelectionChange?: (rows: TractNRI[]) => void;
  /** Configuration for volume source editing and display */
  volumeSourceConfig?: VolumeSourceConfig;
  /** Configuration for AFE source editing and display */
  afeSourceConfig?: AfeSourceConfig;
  /** Server-side export callback. When provided, uses server-side export instead of client-side. */
  onServerExport?: (params: ServerExportParams) => void;
  /** Whether server-side export is in progress */
  isServerExporting?: boolean;
  /** Named column ordering presets for the export dropdown */
  exportPresets?: ExportPreset[];
  /** Whether there are more rows to load */
  hasMore?: boolean;
  /** Callback to load more rows when scrolling near the bottom */
  onLoadMore?: () => void;
  /** Whether more rows are currently being fetched */
  isFetchingMore?: boolean;
  /** Callback for server-side search. When provided, search delegates to server instead of client-side globalFilter */
  onSearchChange?: (search: string) => void;
  /** Controlled search value for the search input (used with onSearchChange) */
  searchValue?: string;
  /** Save handler for the Bulk Edit drawer; receives slot ids and normalized patch. */
  onBulkEditSave?: BulkEditSavePayloadHandler;
  /** Optional resolver for the "original" value displayed in the review screen. */
  resolveBulkEditOriginal?: BulkEditOriginalResolver;
  /** When true, renders the row-selection checkbox column, Edit toolbar button, and Bulk Edit drawer. */
  enableSlotSelection?: boolean;
  /** The package the rows belong to, so an "Excluded from package" reason links to its wizard. */
  packageId?: number;
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  // TractWizard state
  const [isTractWizardOpen, setIsTractWizardOpen] = useState(false);
  const [editTract, setEditTract] = useState<{
    tractId: string | null;
    subtractId: string | null;
  }>({ tractId: null, subtractId: null });
  const tractEnums = useAtomValue(tractEnumLoadableAtom);
  const store = useStore();
  const tractRecordTypes =
    tractEnums.state === 'hasData' && tractEnums.data
      ? tractEnums.data.tractRecordType
      : [];
  const accountType =
    tractEnums.state === 'hasData' && tractEnums.data
      ? tractEnums.data.accountType
      : [];
  const accountSource =
    tractEnums.state === 'hasData' && tractEnums.data
      ? tractEnums.data.accountSource
      : [];

  const handleTractClick = (tractId: string, subtractId?: string) => {
    setEditTract({ tractId, subtractId: subtractId ?? null });
    setIsTractWizardOpen(true);
  };

  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() => {
    if (selectedRow) {
      const index = tractNRIRows.findIndex((row) => row === selectedRow);
      if (index >= 0) {
        return { [String(index)]: true };
      }
    }
    return {};
  });

  const {
    columnFilters,
    setColumnFilters,
    isColumnFilterActive,
    setIsColumnFilterActive,
  } = useColumnFilter(tableId);

  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);

  const resetCustomizations = useCallback(() => {
    setColumnFilters([]);
    setIsColumnFilterActive(false);
  }, [setColumnFilters, setIsColumnFilterActive]);

  const showExpander = subtractNRIColumns.length > 0;
  const columns = useMemo(
    () =>
      getTractNRIColumns(tractNRIColumns, showExpander, enableSlotSelection),
    [tractNRIColumns, showExpander, enableSlotSelection]
  );

  // Create export-only columns with store captured in closure
  const exportOnlyColumns: ColumnDef<TractNRI, any>[] = useMemo(
    () => [
      {
        id: 'nriVerified',
        header: 'NRI Verified',
        size: 130,
        meta: {
          toggleColumnFilter: false,
          getExportValue: (row: TractNRI) => {
            const subtracts = row.subtracts ?? [];
            if (subtracts.length === 0) return 'Not Started';
            const { totalSteps, markedCorrectTotal, markedIncorrectTotal } =
              store.get(
                rollupVerificationAtomFamily({ subtracts, type: 'nri' })
              );
            return getVerificationStatusText(
              totalSteps,
              markedCorrectTotal,
              markedIncorrectTotal
            );
          },
        },
      },
      {
        id: 'wiVerified',
        header: 'WI Verified',
        size: 130,
        meta: {
          toggleColumnFilter: false,
          getExportValue: (row: TractNRI) => {
            const subtracts = row.subtracts ?? [];
            if (subtracts.length === 0) return 'Not Started';
            const { totalSteps, markedCorrectTotal, markedIncorrectTotal } =
              store.get(
                rollupVerificationAtomFamily({ subtracts, type: 'wi' })
              );
            return getVerificationStatusText(
              totalSteps,
              markedCorrectTotal,
              markedIncorrectTotal
            );
          },
        },
      },
    ],
    [store]
  );

  // Combine regular columns with export-only columns at correct positions
  const allColumns = useMemo(
    () =>
      columns.flatMap((col) => {
        const key = col.id ?? (col as { accessorKey?: string }).accessorKey;
        if (key === 'totalSlotNri') return [col, exportOnlyColumns[0]];
        if (key === 'totalSlotWi') return [col, exportOnlyColumns[1]];
        return [col];
      }),
    [columns, exportOnlyColumns]
  );

  // Column ordering and pinning state
  const initialColumnPinning: ColumnPinningState = {
    left: [
      ...(enableSlotSelection ? ['select'] : []),
      ...(showExpander ? ['expander'] : []),
    ],
  };

  const initialColumnOrder = useMemo(
    () => getInitialColumnOrder(columns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId,
      initialColumnOrder,
      initialColumnPinning,
    });

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const parentRef = useRef<HTMLDivElement | null>(null);

  // State to track when the scroll element is mounted (for infinite scroll)
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null
  );

  // Callback ref to capture when the element mounts
  const scrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    parentRef.current = node;
    setScrollElement(node);
  }, []);

  const handleRowSelectionChange = useCallback(
    (
      updater:
        RowSelectionState | ((old: RowSelectionState) => RowSelectionState)
    ) => {
      setRowSelection((old) => {
        const newSelection =
          typeof updater === 'function' ? updater(old) : updater;
        const selectedKeys = Object.keys(newSelection).filter(
          (key) => newSelection[key]
        );
        const selectedRows = selectedKeys
          .map((key) => tractNRIRows[+key])
          .filter((row): row is TractNRI => row !== null && row !== undefined);
        onRowsSelectionChange?.(selectedRows);
        onRowSelected?.(selectedRows[0] ?? null);
        return newSelection;
      });
    },
    [onRowSelected, onRowsSelectionChange, tractNRIRows]
  );

  const table = useReactTable({
    data: tractNRIRows,
    columns: allColumns,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: MIN_WIDTH,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: (row) => {
      const data = row.original;
      return !!((data.subtracts?.length ?? 0) > 0);
    },
    enableColumnResizing: true,
    enableExpanding: true,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    state: {
      globalFilter: onSearchChange ? '' : globalFilter,
      columnFilters,
      columnSizing,
      expanded,
      rowSelection,
      columnOrder,
      columnPinning,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnSizingChange: setColumnSizing,
    onExpandedChange: setExpanded,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onSortingChange: setSorting,
    initialState: {
      columnPinning: initialColumnPinning,
      columnOrder: initialColumnOrder,
      columnVisibility: {
        nriVerified: false, // Export-only, never visible
        wiVerified: false, // Export-only, never visible
      },
    },
    meta: {
      reset: {
        resetCustomizations,
      },
      packageId,
      onTractClick: handleTractClick,
      volumeSourceConfig,
      afeSourceConfig,
    },
  });

  const { rows: parentRows } = table.getRowModel();

  const virtualizer = useVirtualizer({
    count: parentRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const row = parentRows[index];
      if (!row) return CELL_BASE_ROW_HEIGHT;
      const isExpanded = row.getIsExpanded();
      const originalData = row.original;
      if (isExpanded && (originalData.subtracts?.length ?? 0) > 0) {
        return (
          CELL_BASE_ROW_HEIGHT +
          CELL_MARGIN +
          CELL_HEIGHT +
          (originalData.subtracts?.length ?? 0) * CELL_HEIGHT +
          CELL_PADDING +
          CELL_MARGIN
        );
      }
      return CELL_BASE_ROW_HEIGHT;
    },
    overscan: 5,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
  });

  // Same pattern as package SlotsTable: persisted columnSizing; remeasure virtual
  // rows when widths change so wrapped Volume Source / AFE text does not overlap.
  useLayoutEffect(() => {
    virtualizer.measure();
  }, [columnSizing, virtualizer]);

  // Infinite scroll: load more when scrolled near bottom
  useEffect(() => {
    if (!scrollElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;

      // Check conditions inside the handler to avoid stale closures
      if (!hasMore || !onLoadMore || isFetchingMore) return;

      // Load more when within 300px of the bottom
      if (scrollBottom < 300) {
        onLoadMore();
      }
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [scrollElement, hasMore, onLoadMore, isFetchingMore]);

  if (isLoading) {
    return (
      <DataTableSkeleton
        data-testid="tract-table-skeleton"
        columnCount={columns.length}
        rowCount={10}
        showHeader={false}
        showToolbar={false}
      />
    );
  }

  const isColumnVisible = table.getVisibleLeafColumns().length > 0;

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={(e) =>
        handleDragEnd(
          e,
          columnPinning,
          columnOrder,
          setColumnOrder,
          setColumnPinning,
          hoveredColumnIdRef
        )
      }
      modifiers={[restrictToHorizontalAxis]}
      onDragMove={onDragMove}
    >
      <TableContainer className={styles.tableContainer}>
        <TableToolbar>
          <TableToolbarContent>
            <TableToolbarSearch
              persistent
              placeholder="Search"
              onChange={(e: any) => {
                const value = e.target.value;
                if (onSearchChange) {
                  onSearchChange(value);
                } else {
                  setGlobalFilter(value);
                }
              }}
              value={onSearchChange ? (searchValue ?? '') : globalFilter}
            />
            {enableSlotSelection && (
              <Button
                hasIconOnly
                renderIcon={Edit}
                kind="ghost"
                size="lg"
                iconDescription="Edit"
                data-testid="nri-table-edit-button"
                disabled={table.getSelectedRowModel().rows.length === 0}
                onClick={() => setIsEditDrawerOpen(true)}
              />
            )}
            <TableActions
              table={table}
              isColumnFilterActive={isColumnFilterActive}
              setIsColumnFilterActive={setIsColumnFilterActive}
              showExport
              onServerExport={onServerExport}
              isServerExporting={isServerExporting}
              exportPresets={exportPresets}
            />
          </TableToolbarContent>
        </TableToolbar>
        {isColumnVisible ? (
          <>
            <div className={styles.tableWrapper} ref={scrollContainerRef}>
              <Table>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Fragment key={headerGroup.id}>
                      <tr>
                        <SortableContext
                          items={columnOrder}
                          strategy={horizontalListSortingStrategy}
                        >
                          {headerGroup.headers.map((header) => {
                            if (header.column.id === 'select') {
                              return (
                                <th
                                  key={header.id}
                                  style={{
                                    padding: 0,
                                    verticalAlign: 'middle',
                                    ...getColumnStyle(header.column.getSize()),
                                    ...getPinningStyles(header.column),
                                  }}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                </th>
                              );
                            }
                            return (
                              <DragHeader
                                key={header.id}
                                header={header}
                                hoveredColumnIdRef={hoveredColumnIdRef}
                              />
                            );
                          })}
                        </SortableContext>
                      </tr>
                      {isColumnFilterActive && (
                        <tr className={styles.filterRow}>
                          <ColumnFilter
                            headers={headerGroup.headers}
                            pinStyleGenerator={getPinningStyles}
                          />
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </thead>
                <tbody style={{ height: `${virtualizer.getTotalSize()}px` }}>
                  {parentRows.length === 0 ? (
                    <TableRow className={styles.emptyStateRow}>
                      <TableCell colSpan={columns.length}>
                        <EmptyState
                          icon={NoDataSvg}
                          headerText="No tract data available"
                          helperText="Try adjusting your search or filters"
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    virtualizer.getVirtualItems().map((virtualRow) => {
                      const row = parentRows[virtualRow.index];
                      const isExpanded = row.getIsExpanded();
                      const isSelected = row.getIsSelected();
                      const originalData = row.original;
                      const visibleCells = row.getVisibleCells();
                      const lastCellIndex = visibleCells.length - 1;

                      return (
                        <tr
                          key={row.id}
                          data-index={virtualRow.index}
                          ref={(node) => virtualizer.measureElement(node)}
                          className={
                            isSelected ? styles.selectedRow : undefined
                          }
                          style={{
                            position: 'absolute',
                            transform: `translateY(${virtualRow.start}px)`,
                            width: '100%',
                            backgroundColor: '#ffffff',
                          }}
                        >
                          {visibleCells.map((cell, cellIndex) => {
                            const isLastCell = cellIndex === lastCellIndex;
                            const widthBefore = visibleCells
                              .slice(0, cellIndex)
                              .reduce((sum, c) => sum + c.column.getSize(), 0);

                            return (
                              <td
                                key={cell.id}
                                className={classNames('bx--table-cell', {
                                  [styles.tableCellPinned]:
                                    cell.column.getIsPinned(),
                                })}
                                style={{
                                  ...getColumnStyle(cell.column.getSize()),
                                  ...getPinningStyles(cell.column),
                                  verticalAlign: 'top',
                                }}
                              >
                                <div>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                  {isLastCell &&
                                    isExpanded &&
                                    originalData.subtracts && (
                                      <div
                                        className={styles.expandedContent}
                                        style={{
                                          position: 'absolute',
                                          left: `-${widthBefore}px`,
                                          top: '100%',
                                          marginBlock: '16px',
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <SubtractNRITable
                                          subtractRows={originalData.subtracts}
                                          subtractColumns={subtractNRIColumns}
                                          onTractClick={handleTractClick}
                                        />
                                      </div>
                                    )}
                                </div>
                                {isLastCell &&
                                  isExpanded &&
                                  originalData.subtracts && (
                                    <div
                                      style={{
                                        height: `${
                                          CELL_BASE_ROW_HEIGHT +
                                          CELL_MARGIN +
                                          originalData.subtracts.length *
                                            CELL_HEIGHT +
                                          CELL_PADDING +
                                          CELL_MARGIN
                                        }px`,
                                        visibility: 'hidden',
                                      }}
                                    />
                                  )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
            {isFetchingMore && (
              <div style={{ padding: '1rem', textAlign: 'center' }}>
                <InlineLoading description="Loading more rows..." />
              </div>
            )}
            <div className={styles.totalRowCount}>
              <label className={styles.totalRows}>
                {DECIMAL_FORMATTER.format(
                  table.getFilteredRowModel().rows.length
                )}
              </label>
              {plur('row', table.getFilteredRowModel().rows.length)}
              {hasMore && ' (more available)'}
            </div>
          </>
        ) : (
          <HiddenColumns className={styles.hiddenColumn} />
        )}
        <TractWizard
          isOpen={isTractWizardOpen}
          onClose={() => {
            setIsTractWizardOpen(false);
            setEditTract({ tractId: null, subtractId: null });
          }}
          tractRecordTypes={tractRecordTypes}
          accountType={accountType}
          accountSource={accountSource}
          editTract={editTract}
        />
        {enableSlotSelection && (
          <BulkEditSlotsPanelConnected
            open={isEditDrawerOpen}
            onClose={() => setIsEditDrawerOpen(false)}
            selectedRows={table
              .getSelectedRowModel()
              .rows.map((r) => r.original)}
            allRows={table.getCoreRowModel().rows.map((r) => r.original)}
            onSave={onBulkEditSave}
            resolveOriginal={resolveBulkEditOriginal}
          />
        )}
      </TableContainer>
    </DndContext>
  );
};
